import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, CheckCircle2, Clock3, ImageIcon } from "lucide-react";

/**
 * iter181 — Client Payment-Screenshot Upload widget.
 *
 * Lives inside the existing MAKE A PAYMENT section on the Client
 * Tracker. Uploading a screenshot never mutates the payment ledger;
 * approval is purely a proof signal reviewed by the studio admin.
 *
 * Props:
 *   token       — tracker token (capability).
 *   milestones  — from data.milestones. Optional; falls back to "other".
 */
const MAX_MB = 5;
const ALLOWED = ["image/jpeg", "image/png", "image/webp"];

export default function PaymentProofUploader({ token, milestones = [], preselectMilestoneId = null }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [milestoneId, setMilestoneId] = useState("other");
  const [uploading, setUploading] = useState(false);
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highlight, setHighlight] = useState(false);
  const inputRef = useRef(null);
  const sectionRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/track/${encodeURIComponent(token)}/payment-proofs`);
      setExisting(data.proofs || []);
    } catch { /* silent — new tracker, no proofs yet */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  // iter182 — When parent (PayNowModal confirmation) preselects a
  // milestone, sync radio, scroll into view, and flash a highlight so
  // the client notices where to upload their screenshot.
  useEffect(() => {
    if (!preselectMilestoneId) return;
    setMilestoneId(preselectMilestoneId);
    // Defer the scroll slightly so the modal close animation lands first.
    const t = setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setHighlight(true);
    }, 120);
    const t2 = setTimeout(() => setHighlight(false), 2200);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [preselectMilestoneId]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Please upload a JPG, PNG or WEBP image.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image is too large — please keep it under ${MAX_MB} MB.`);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const removeFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    if (milestoneId) form.append("milestone_id", milestoneId);
    setUploading(true);
    try {
      await axios.post(
        `${API}/public/track/${encodeURIComponent(token)}/payment-proofs`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Payment proof submitted");
      removeFile();
      setMilestoneId("other");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — please try again.");
    } finally { setUploading(false); }
  };

  const badgeCls = (s) => {
    if (s === "APPROVED") return "bg-[#E7EFE7] text-[#3F6B4A] border-[#c9dcc9]";
    if (s === "REJECTED") return "bg-[#F5DBD3] text-[#B04A32] border-[#DBB8AA]";
    return "bg-[#EDE5D6] text-[#7A5A2E] border-[#dbc9a3]";
  };
  const badgeIcon = (s) => (s === "APPROVED" ? <CheckCircle2 className="w-3 h-3" strokeWidth={2.2} /> : <Clock3 className="w-3 h-3" strokeWidth={2.2} />);

  return (
    <section
      ref={sectionRef}
      id="payment-proof-uploader"
      className={`mt-6 pt-6 border-t border-[#E7D5B4]/60 transition-all duration-500 ${highlight ? "bg-[#F5EBDC]/60 -mx-4 md:-mx-6 px-4 md:px-6 rounded-md ring-1 ring-[#9C7550]/40" : ""}`}
      data-testid="tracker-payment-proof-uploader"
    >
      <div className="editorial-eyebrow text-[#9C7550] flex items-center gap-1.5 mb-3">
        <ImageIcon className="w-3 h-3" strokeWidth={2} /> Upload Payment Screenshot
      </div>

      {/* Milestone selector — falls back to "Other / Not sure" when no
          milestones exist for this booking yet. */}
      {milestones && milestones.length > 0 && (
        <div className="space-y-1.5 mb-4" data-testid="tracker-proof-milestone-picker">
          <div className="text-[10px] uppercase tracking-widest text-[#B18860] font-semibold">Which payment is this for?</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {milestones.map((m) => (
              <label key={m.id} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors ${milestoneId === m.id ? "border-[#9C7550] bg-[#FDF7EF]" : "border-[#E7D5B4]/70 hover:bg-[#FDF7EF]/50"}`} data-testid={`tracker-proof-milestone-${m.milestone_type || m.id}`}>
                <input
                  type="radio"
                  name="pp-milestone"
                  value={m.id}
                  checked={milestoneId === m.id}
                  onChange={() => setMilestoneId(m.id)}
                  className="accent-[#9C7550]"
                />
                <span className="flex-1 min-w-0 truncate text-[#3B2F24]">{m.label || m.milestone_type}</span>
                <span className="text-[#9C7550] font-mono tabular-nums text-xs">
                  ₹{Number(m.amount || 0).toLocaleString("en-IN")}
                </span>
              </label>
            ))}
            <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${milestoneId === "other" ? "border-[#9C7550] bg-[#FDF7EF]" : "border-[#E7D5B4]/70 hover:bg-[#FDF7EF]/50"}`} data-testid="tracker-proof-milestone-other">
              <input
                type="radio"
                name="pp-milestone"
                value="other"
                checked={milestoneId === "other"}
                onChange={() => setMilestoneId("other")}
                className="accent-[#9C7550]"
              />
              <span className="flex-1 text-[#3B2F24]">Other / Not sure</span>
            </label>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={onPick}
        data-testid="tracker-proof-file-input"
      />

      {!file ? (
        <button
          type="button"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-dashed border-[#9C7550] text-[#9C7550] hover:bg-[#FDF7EF] transition-colors"
          onClick={() => inputRef.current?.click()}
          data-testid="tracker-proof-pick-btn"
        >
          <Upload className="w-4 h-4" strokeWidth={2} />
          Choose screenshot (JPG, PNG, WEBP · max {MAX_MB} MB)
        </button>
      ) : (
        <div className="border border-[#E7D5B4]/70 rounded-md p-3 flex items-start gap-3" data-testid="tracker-proof-preview">
          <img src={previewUrl} alt="Preview" className="w-24 h-24 object-cover rounded-md border border-[#E7D5B4]/70" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-[#3B2F24] truncate" data-testid="tracker-proof-filename">
              {file.name}
            </div>
            <div className="text-[11px] text-[#B18860] mt-0.5">
              {(file.size / 1024).toFixed(1)} KB · {file.type.replace("image/", "").toUpperCase()}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={submit}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#3B2F24] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#231b13] disabled:opacity-50"
                data-testid="tracker-proof-submit"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" strokeWidth={2} />}
                {uploading ? "Uploading…" : "Submit proof"}
              </button>
              <button
                type="button"
                onClick={removeFile}
                disabled={uploading}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[11px] text-[#B04A32] hover:bg-[#F5DBD3] uppercase tracking-widest font-semibold"
                data-testid="tracker-proof-remove"
              >
                <X className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && existing.length > 0 && (
        <div className="mt-5" data-testid="tracker-proof-history">
          <div className="text-[10px] uppercase tracking-widest text-[#B18860] font-semibold mb-2">
            Submitted proofs ({existing.length})
          </div>
          <ul className="space-y-2">
            {existing.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-[#E7D5B4]/70 bg-[#FDF7EF]/40"
                data-testid={`tracker-proof-item-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#3B2F24] truncate">
                    {p.milestone_label || "Other / Not sure"}
                  </div>
                  <div className="text-[10px] text-[#B18860]">
                    {new Date(p.uploaded_at).toLocaleString("en-IN")}
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-semibold ${badgeCls(p.status)}`}>
                  {badgeIcon(p.status)} {p.status}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] italic text-[#6E5B49] mt-3">
            We've received your payment screenshot. Our team will verify the payment and update your payment status.
          </p>
        </div>
      )}
    </section>
  );
}
